// numberFormat.js — the one place the frontend shows a number or an amount
// of money, or reads back a number a user typed (vis_localisation.md,
// ui-architecture.md "Numbers and Currency").
//
// Every number follows the company Number Format (Company Settings >
// Localisation: 1,234.56, 1.234,56 or 1 234,56) and every amount the
// Currency Label (Symbol, Symbol + prefix or Code). Only the home currency
// (Company Settings > Default Currency) ever shows as a bare symbol; any
// other currency is always marked (US$1,234.56 / USD 1,234.56), so a
// supplier's USD cost can't be mistaken for AUD. Never format a number with
// toLocaleString('en-AU'), toFixed or a hard-coded '$'.
//
// Stored values and the API stay plain numbers (1234.56): formatting is
// display only, and parseNumberText turns typed text back into that form.
//
// The settings are module state: App.js sets them from Company Settings
// (and again after Company Settings is saved) before rendering any screen,
// and the server-rendered Pricelist PDF sets them from companyInfo
// (pricelistPdfEntry.js). Until then they fall back to 1,234.56, Symbol and
// AUD. Mirrors backend/utils/numbers.js.

// The company Number Format choices — same list as backend/utils/numbers.js.
// The space style groups with a no-break space so an amount never wraps.
export const NUMBER_FORMATS = [
  { value: '1,234.56', label: '1,234.56', group: ',', decimal: '.' },
  { value: '1.234,56', label: '1.234,56', group: '.', decimal: ',' },
  { value: '1 234,56', label: '1 234,56', group: '\u00A0', decimal: ',' },
];
const DEFAULT_NUMBER_FORMAT = '1,234.56';

// The Currency Label choices — same list as backend/utils/numbers.js.
export const CURRENCY_LABELS = [
  { value: 'symbol', label: 'Symbol ($1,234.56)' },
  { value: 'symbol_prefix', label: 'Symbol + prefix (A$1,234.56)' },
  { value: 'code', label: 'Code (AUD 1,234.56)' },
];
const DEFAULT_CURRENCY_LABEL = 'symbol';
const DEFAULT_HOME_CURRENCY = 'AUD';

// Where Intl's own en-US symbol for a currency is a bare '$' or a code,
// the marked symbol people actually write.
const PREFIX_SYMBOL_OVERRIDES = { USD: 'US$', SGD: 'S$' };

let tenantNumberFormat = DEFAULT_NUMBER_FORMAT;
let tenantCurrencyLabel = DEFAULT_CURRENCY_LABEL;
let tenantHomeCurrency = DEFAULT_HOME_CURRENCY;

export function setTenantNumberFormat(format) {
  tenantNumberFormat = NUMBER_FORMATS.some((f) => f.value === format) ? format : DEFAULT_NUMBER_FORMAT;
}

export function getTenantNumberFormat() {
  return tenantNumberFormat;
}

export function setTenantCurrencyLabel(label) {
  tenantCurrencyLabel = CURRENCY_LABELS.some((l) => l.value === label) ? label : DEFAULT_CURRENCY_LABEL;
}

export function getTenantCurrencyLabel() {
  return tenantCurrencyLabel;
}

export function setTenantHomeCurrency(code) {
  tenantHomeCurrency = isCurrencyCode(code) ? code.toUpperCase() : DEFAULT_HOME_CURRENCY;
}

export function getTenantHomeCurrency() {
  return tenantHomeCurrency;
}

function isCurrencyCode(code) {
  if (typeof code !== 'string' || !/^[A-Za-z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
    return true;
  } catch {
    return false;
  }
}

// { group, decimal } for the company Number Format.
export function numberSeparators() {
  const f = NUMBER_FORMATS.find((n) => n.value === tenantNumberFormat) || NUMBER_FORMATS[0];
  return { group: f.group, decimal: f.decimal };
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

// Any number in the company Number Format — '' for blank or not a number.
// `decimals` fixes the decimal places (money, rates); otherwise
// `minDecimals`/`maxDecimals` bound them (quantities show only what they
// have). `grouping: false` drops the thousands separator.
export function formatNumber(value, { decimals, minDecimals, maxDecimals, grouping = true } = {}) {
  const n = toNumber(value);
  if (n === null) return '';
  const min = decimals ?? minDecimals ?? 0;
  const max = decimals ?? Math.max(maxDecimals ?? Math.max(min, 3), min);
  const en = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: grouping,
  }).format(n);
  const { group, decimal } = numberSeparators();
  // en-US writes 1,234.56; swap in the company's separators in one pass.
  return en.replace(/[,.]/g, (c) => (c === ',' ? group : decimal));
}

// A percentage: formatPercent(12.5) -> '12.50%'.
export function formatPercent(value, decimals = 2) {
  const text = formatNumber(value, { decimals });
  return text ? `${text}%` : '';
}

function bareSymbol(code) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
    .formatToParts(0).find((p) => p.type === 'currency').value;
}

function prefixSymbol(code) {
  if (PREFIX_SYMBOL_OVERRIDES[code]) return PREFIX_SYMBOL_OVERRIDES[code];
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'symbol' })
    .formatToParts(0).find((p) => p.type === 'currency').value;
}

// The label an amount in `currency` carries under the Currency Label
// setting: '$', 'A$', 'US$' or 'AUD'. Only the home currency can be a bare
// symbol; a foreign one is always marked, and falls back to its code if
// its marked symbol would read as the home currency's.
export function currencyLabel(currency = tenantHomeCurrency) {
  const code = isCurrencyCode(currency) ? currency.toUpperCase() : tenantHomeCurrency;
  if (tenantCurrencyLabel === 'code') return code;
  if (code === tenantHomeCurrency) {
    return tenantCurrencyLabel === 'symbol' ? bareSymbol(code) : prefixSymbol(code);
  }
  const marked = prefixSymbol(code);
  const homeBare = bareSymbol(tenantHomeCurrency);
  return marked === homeBare || marked === '$' ? code : marked;
}

// An amount of money: formatMoney(1234.5) -> '$1,234.50';
// formatMoney(99, { currency: 'USD' }) -> 'US$99.00'. '' for blank.
// `decimals` defaults to 2.
export function formatMoney(value, { currency, decimals = 2 } = {}) {
  const n = toNumber(value);
  if (n === null) return '';
  const label = currencyLabel(currency);
  const amount = formatNumber(Math.abs(n), { decimals });
  // A code or a lettered symbol (CHF) gets a no-break space, so it never
  // wraps away from its amount; $, A$, € sit tight.
  const joined = /[A-Za-z]$/.test(label) ? `${label}\u00A0${amount}` : `${label}${amount}`;
  // A value that rounds to zero shows no minus sign.
  return n < 0 && Number(amount.replace(/\D/g, '')) !== 0 ? `-${joined}` : joined;
}

// A number as it sits in an entry field while it's being edited: company
// decimal separator, no thousands separator, every decimal place kept.
// '' for blank.
export function toInputText(value) {
  if (value === null || value === undefined || value === '') return '';
  const plain = typeof value === 'number' ? String(value) : String(value).trim();
  if (!/^-?\d*\.?\d*$/.test(plain)) return plain;
  return plain.replace('.', numberSeparators().decimal);
}

// Keeps only what can be part of a number in the company style — digits,
// the separators, and a leading minus when allowed — so a stray letter
// never lands in a number field. `decimals: 0` drops the decimal separator.
export function filterNumberText(text, { allowNegative = false, decimals } = {}) {
  const { group, decimal } = numberSeparators();
  const allowed = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', group]);
  if (decimals !== 0) allowed.add(decimal);
  // The space style also takes an ordinary space for grouping, and a
  // full stop as the decimal, since neither can mean anything else there.
  if (group === '\u00A0') { allowed.add(' '); allowed.add('\u202F'); if (decimals !== 0) allowed.add('.'); }
  let out = '';
  for (const c of String(text ?? '')) {
    if (allowed.has(c)) out += c;
    else if (c === '-' && allowNegative && out === '') out += c;
  }
  return out;
}

// What a user typed into a number field -> the plain '1234.56' form
// (a string, so trailing zeros survive like a native number input), '' for
// blank, or null when it isn't a number. Thousands separators are ignored
// wherever they sit.
export function parseNumberText(text) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return '';
  const { group, decimal } = numberSeparators();
  let s = trimmed;
  if (group === '\u00A0') s = s.replace(/[\s\u00A0\u202F]/g, '').replace('.', ',');
  else s = s.split(group).join('');
  s = s.replace(decimal, '.');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  if (s.endsWith('.')) s = s.slice(0, -1);
  if (s.startsWith('.')) s = `0${s}`;
  if (s.startsWith('-.')) s = `-0${s.slice(1)}`;
  return s;
}
